<template>
  <v-dialog
    :model-value="modelValue"
    max-width="800px"
    persistent
    scrollable
    data-testid="tq-item-dialog"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <v-card @keydown.esc="closeDialog">
      <v-card-title class="bg-primary text-white d-flex align-center pa-4">
        <v-icon class="mr-2">{{ isEditMode ? 'mdi-pencil' : 'mdi-plus-circle' }}</v-icon>
        <div class="flex-grow-1">
          <div class="text-h6">{{ isEditMode ? 'แก้คำถาม (opening question)' : 'เพิ่มคำถาม (opening question)' }}</div>
          <div class="text-caption opacity-80">
            การเปลี่ยนแปลงจะถูกพักไว้ — กด SAVE ที่หน้า builder เพื่อบันทึกถาวร
          </div>
        </div>
        <v-btn icon variant="text" color="white" @click="closeDialog">
          <v-icon>mdi-close</v-icon>
          <v-tooltip activator="parent" location="top">ปิด</v-tooltip>
        </v-btn>
      </v-card-title>

      <v-card-text class="pa-6">
        <v-alert
          v-if="submitAttempted && validationErrors.length"
          type="error"
          variant="tonal"
          density="compact"
          class="mb-4"
          data-testid="tq-item-dialog-error"
        >
          <div class="font-weight-medium mb-1">กรุณาตรวจสอบข้อมูลให้ครบถ้วน</div>
          <ul class="tq-error-list">
            <li v-for="(msg, i) in validationErrors" :key="i">{{ msg }}</li>
          </ul>
        </v-alert>

        <!-- ── ชนิดคำถาม (question type) — drives the emitted ItemKind + which blocks
             render below. Single-field types → SCALAR item; group → GROUP_INPUT;
             income_classifier → INCOME_CLASSIFIER; layout → LAYOUT. -->
        <div class="tq-block">
          <h4 class="tq-block-title">ชนิดคำถาม</h4>
          <v-select
            :key="typeSelectKey"
            data-testid="tq-item-type"
            :model-value="qType"
            :items="questionTypeOptions"
            item-title="title"
            item-value="value"
            label="ชนิดคำถาม *"
            variant="outlined"
            density="comfortable"
            prepend-inner-icon="mdi-shape-outline"
            hide-details
            @update:model-value="onTypeChange"
          />
          <p class="text-caption text-medium-emphasis mt-2 mb-0">{{ questionTypeHint }}</p>

          <!-- 🆕 R6 — re-file this question into another Section without leaving the dialog.
               The move applies on submit (item.groupCode flows through onDialogSubmit). -->
          <v-select
            v-if="sectionOptions.length"
            data-testid="tq-item-section"
            :model-value="currentSectionValue"
            :items="sectionOptions"
            item-title="title"
            item-value="value"
            label="หมวด (Section)"
            variant="outlined"
            density="comfortable"
            prepend-inner-icon="mdi-folder-outline"
            hide-details
            class="mt-3"
            @update:model-value="onSectionChange"
          />
        </div>

        <!-- ── BLOCK: ข้อมูลเชื่อมโยง (สิ่งที่คำถามนี้เก็บ) ──────────── -->
        <div class="tq-block">
          <h4 class="tq-block-title">ข้อมูลเชื่อมโยง</h4>

          <!-- LAYOUT → input type -->
          <v-select
            v-if="isLayout"
            data-testid="tq-item-inputtype"
            v-model="item.inputType"
            :items="inputTypeOptions"
            label="ชนิดส่วนจัดวาง (input type) *"
            variant="outlined"
            density="comfortable"
            prepend-inner-icon="mdi-form-textbox"
          />

          <!-- 🆕 INCOME_CLASSIFIER → author a catalog of income categories (§40 types).
               Each row: code (verbatim), labelTh (shown/echoed), flagKey (the yes/no
               flag written per category at runtime, verbatim), keywords (chips — hints
               the LLM uses to classify). Round-trips as item.incomeCategories with the
               store (buildItemRequest / mapItemDtoToUi). -->
          <div v-else-if="isIncomeClassifier" class="tq-income-block">
            <div class="tq-options-header">
              <span class="tq-label">
                ประเภทรายได้ที่ให้ AI จำแนก (แต่ละประเภท: รหัส + ชื่อ + flag + คำที่เกี่ยวข้อง)
                <span class="tq-label-flag"> · ต้องมีอย่างน้อย 1 ประเภท (กรอกรหัส/flag/ชื่อ ครบ)</span>
              </span>
              <v-btn
                data-testid="tq-item-income-add"
                size="small"
                variant="outlined"
                color="primary"
                @click="addIncomeCategory"
              >
                <v-icon start>mdi-plus</v-icon>
                เพิ่มประเภทรายได้
              </v-btn>
            </div>

            <div v-if="item.incomeCategories && item.incomeCategories.length" class="tq-income-list">
              <div v-for="(cat, idx) in item.incomeCategories" :key="idx" class="tq-income-row">
                <span class="tq-option-index">{{ idx + 1 }}</span>
                <div class="tq-income-main">
                  <v-text-field
                    :data-testid="`tq-item-income-code-${idx}`"
                    v-model="cat.code"
                    label="รหัสประเภท (code) *"
                    placeholder="เช่น 40_1"
                    variant="outlined"
                    density="compact"
                    hide-details
                    prepend-inner-icon="mdi-identifier"
                    class="tq-income-code"
                  />
                  <v-text-field
                    :data-testid="`tq-item-income-labelth-${idx}`"
                    v-model="cat.labelTh"
                    label="ชื่อประเภท (ภาษาไทย) *"
                    placeholder="เช่น เงินเดือน/ค่าจ้าง"
                    variant="outlined"
                    density="compact"
                    hide-details
                    class="tq-income-label"
                  />
                  <v-text-field
                    :data-testid="`tq-item-income-flagkey-${idx}`"
                    v-model="cat.flagKey"
                    label="Flag key *"
                    placeholder="เช่น type_40_1"
                    variant="outlined"
                    density="compact"
                    hide-details
                    prepend-inner-icon="mdi-flag-variant"
                    hint="เขียนค่า yes/no ที่ runtime; ใช้อ้างใน VisibleWhen เช่น type_40_1 = yes"
                    class="tq-income-flag"
                  />
                  <v-combobox
                    :data-testid="`tq-item-income-keywords-${idx}`"
                    v-model="cat.keywords"
                    label="คำที่เกี่ยวข้อง (keywords)"
                    placeholder="พิมพ์แล้วกด Enter"
                    multiple
                    chips
                    closable-chips
                    :return-object="false"
                    variant="outlined"
                    density="compact"
                    hide-details
                    class="tq-income-keywords"
                  />
                </div>
                <div class="tq-income-actions">
                  <v-btn size="small" density="comfortable" variant="text" icon aria-label="เลื่อนขึ้น" :disabled="idx === 0" @click="moveIncomeCategory(idx, -1)">
                    <v-icon size="18">mdi-arrow-up</v-icon>
                    <v-tooltip activator="parent" location="top">เลื่อนขึ้น</v-tooltip>
                  </v-btn>
                  <v-btn size="small" density="comfortable" variant="text" icon aria-label="เลื่อนลง" :disabled="idx === item.incomeCategories.length - 1" @click="moveIncomeCategory(idx, 1)">
                    <v-icon size="18">mdi-arrow-down</v-icon>
                    <v-tooltip activator="parent" location="top">เลื่อนลง</v-tooltip>
                  </v-btn>
                  <v-btn
                    :data-testid="`tq-item-income-remove-${idx}`"
                    size="small"
                    density="comfortable"
                    variant="text"
                    icon
                    aria-label="ลบประเภท"
                    @click="removeIncomeCategory(idx)"
                  >
                    <v-icon size="18">mdi-close</v-icon>
                    <v-tooltip activator="parent" location="top">ลบ</v-tooltip>
                  </v-btn>
                </div>
              </div>
            </div>
            <p v-else class="text-caption text-medium-emphasis mb-0">
              ยังไม่มีประเภทรายได้ — กด "เพิ่มประเภทรายได้" เพื่อเพิ่มอย่างน้อย 1 ประเภท
            </p>
          </div>

          <!-- 🆕 GROUP → bind MANY fields (mixed Tax Rule + personal + special), each
               with its own "required" toggle. One question, one free-text answer; the
               LLM splits it back into these fields at runtime. -->
          <div v-else-if="isGroupInput" class="tq-boundfields-block">
            <div class="tq-options-header">
              <span class="tq-label">
                Field ที่คำถามนี้ผูก (ผสม Tax Rule + ข้อมูลส่วนตัว + ช่องพิเศษ ได้)
                <span class="tq-label-flag"> · ต้องมีอย่างน้อย 1 field</span>
              </span>
              <v-btn
                data-testid="tq-item-boundfield-add"
                size="small"
                variant="outlined"
                color="primary"
                @click="addBoundField"
              >
                <v-icon start>mdi-plus</v-icon>
                เพิ่ม field
              </v-btn>
            </div>

            <div v-if="item.boundFields && item.boundFields.length" class="tq-boundfield-list">
              <div v-for="(row, idx) in item.boundFields" :key="idx" class="tq-boundfield-row">
                <span class="tq-option-index">{{ idx + 1 }}</span>
                <div class="tq-boundfield-main">
                  <v-autocomplete
                    :data-testid="`tq-item-boundfield-target-${idx}`"
                    :model-value="row.ruleCode || row.fieldKey || null"
                    :items="boundFieldPoolItems"
                    item-title="title"
                    item-value="value"
                    :return-object="false"
                    label="ผูกกับ Tax Rule / field *"
                    :loading="loadingRules"
                    variant="outlined"
                    density="compact"
                    hide-details
                    prepend-inner-icon="mdi-variable"
                    @update:model-value="onBoundFieldSelect(idx, $event)"
                  />
                  <v-switch
                    :data-testid="`tq-item-boundfield-required-${idx}`"
                    :model-value="row.isRequired"
                    label="จำเป็นต้องตอบ"
                    color="primary"
                    inset
                    hide-details
                    density="compact"
                    class="tq-boundfield-required"
                    @update:model-value="setBoundFieldRequired(idx, $event)"
                  />
                </div>
                <v-btn size="small" density="comfortable" variant="text" icon aria-label="เลื่อนขึ้น" :disabled="idx === 0" @click="moveBoundField(idx, -1)">
                  <v-icon size="18">mdi-arrow-up</v-icon>
                  <v-tooltip activator="parent" location="top">เลื่อนขึ้น</v-tooltip>
                </v-btn>
                <v-btn size="small" density="comfortable" variant="text" icon aria-label="เลื่อนลง" :disabled="idx === item.boundFields.length - 1" @click="moveBoundField(idx, 1)">
                  <v-icon size="18">mdi-arrow-down</v-icon>
                  <v-tooltip activator="parent" location="top">เลื่อนลง</v-tooltip>
                </v-btn>
                <v-btn
                  :data-testid="`tq-item-boundfield-remove-${idx}`"
                  size="small"
                  density="comfortable"
                  variant="text"
                  icon
                  aria-label="ลบ field"
                  @click="removeBoundField(idx)"
                >
                  <v-icon size="18">mdi-close</v-icon>
                  <v-tooltip activator="parent" location="top">ลบ</v-tooltip>
                </v-btn>
              </div>
            </div>
            <p v-else class="text-caption text-medium-emphasis mb-0">
              ยังไม่มี field ในกลุ่มนี้ — กด "เพิ่ม field" เพื่อผูกอย่างน้อย 1 field
            </p>
          </div>

          <!-- 🆕 CONFIRM → a summary/confirmation checkpoint. Binds NO Tax Rule /
               personal field; it carries a stable confirm key (fieldKey) + two
               authored prompts: questionSystemPrompt = how to summarize the collected
               answers and ask ยืนยัน/แก้ไข, answerSystemPrompt = how to read the reply
               (ยืนยัน vs แก้ไข + which item to edit). The intro/summary line is authored
               in ข้อความคำถาม below; visibility + เปิดใช้งาน live in the display block. -->
          <div v-else-if="isConfirm" class="tq-confirm-block">
            <v-text-field
              data-testid="tq-item-confirm-fieldkey"
              v-model="item.fieldKey"
              label="Confirm key (fieldKey) *"
              placeholder="เช่น confirm_income_types"
              variant="outlined"
              density="comfortable"
              prepend-inner-icon="mdi-identifier"
              hint="คีย์คงที่ของจุดยืนยันนี้ — ใช้เก็บสถานะ 'ยืนยันแล้ว' (ไม่ผูกกฎภาษี/ข้อมูลส่วนตัว)"
              persistent-hint
            />
            <v-textarea
              data-testid="tq-item-confirm-questionprompt"
              v-model="item.questionSystemPrompt"
              label="System prompt — สรุป + ถามยืนยัน (Question)"
              placeholder="บอก AI ว่าจะสรุปคำตอบที่เก็บมาอย่างไร แล้วถามผู้ใช้ให้เลือก 'ยืนยัน' หรือ 'แก้ไข'"
              variant="outlined"
              density="comfortable"
              rows="3"
              auto-grow
              hide-details
              class="mt-4"
            />
            <v-textarea
              data-testid="tq-item-confirm-answerprompt"
              v-model="item.answerSystemPrompt"
              label="System prompt — อ่านคำตอบ ยืนยัน/แก้ไข (Answer)"
              placeholder="บอก AI ว่าจะตีความคำตอบของผู้ใช้เป็น 'ยืนยัน' หรือ 'แก้ไข' และถ้าแก้ไข จะแก้รายการไหน"
              variant="outlined"
              density="comfortable"
              rows="3"
              auto-grow
              hide-details
              class="mt-4"
            />
          </div>

          <!-- 🆕 SINGLE-FIELD (ใช่/ไม่ใช่ · จำนวนเงิน · ตัวเลือก) → bind exactly ONE
               field. Held in boundFields[0] while editing; materialized to a scalar
               RULE_INPUT (ruleCode) / PERSONAL_INFO|SPECIAL_INPUT (fieldKey) on submit. -->
          <div v-else class="tq-boundfields-block">
            <span class="tq-label mb-1">ผูกกับ Tax Rule / field *</span>
            <v-autocomplete
              data-testid="tq-item-singlebind"
              :model-value="singleBindValue"
              :items="boundFieldPoolItems"
              item-title="title"
              item-value="value"
              :return-object="false"
              label="ผูกกับ Tax Rule / field *"
              :loading="loadingRules"
              variant="outlined"
              density="comfortable"
              hide-details
              prepend-inner-icon="mdi-variable"
              @update:model-value="onBoundFieldSelect(0, $event)"
            />
            <div class="d-flex align-center justify-space-between flex-wrap ga-2 mt-2">
              <p v-if="qType === 'yes_no'" class="text-caption text-medium-emphasis mb-0 d-flex align-center ga-1">
                <v-icon size="14">mdi-information-outline</v-icon>
                <span>คำตอบจะถูกเก็บเป็น yes / no</span>
              </p>
              <v-spacer v-else />
              <v-switch
                data-testid="tq-item-singlebind-required"
                :model-value="(item.boundFields[0] && item.boundFields[0].isRequired) || false"
                label="จำเป็นต้องตอบ"
                color="primary"
                inset
                hide-details
                density="compact"
                @update:model-value="setBoundFieldRequired(0, $event)"
              />
            </div>
          </div>

          <!-- PND form tags -->
          <div v-if="!isLayout && !isConfirm" class="tq-pnd-block">
            <span class="tq-label">ใช้กับแบบ (PND)</span>
            <div class="d-flex flex-wrap ga-4 mt-1">
              <v-checkbox
                v-for="code in pndCodes"
                :key="code"
                :data-testid="`tq-item-pnd-${code}`"
                :model-value="item.pndCodes.includes(code)"
                :label="code"
                density="comfortable"
                hide-details
                @update:model-value="togglePnd(code)"
              />
            </div>
            <ul class="tq-pnd-legend">
              <li v-for="code in pndCodes" :key="code">{{ pndLegend[code] || code }}</li>
            </ul>
          </div>
        </div>

        <!-- ── BLOCK 2: การแสดงผลและเงื่อนไข ──────────────────────── -->
        <div class="tq-block">
          <h4 class="tq-block-title">การแสดงผลและเงื่อนไข</h4>
          <span class="tq-label mb-1">เงื่อนไขการแสดงคำถาม</span>
          <v-btn-toggle
            data-testid="tq-item-visiblemode"
            v-model="vwMode"
            mandatory
            divided
            density="comfortable"
            color="primary"
            variant="outlined"
            class="tq-vw-toggle mb-3"
          >
            <v-btn value="always" size="small">
              <v-icon start size="16">mdi-eye</v-icon>แสดงเสมอ
            </v-btn>
            <v-btn value="conditional" size="small">
              <v-icon start size="16">mdi-filter-variant</v-icon>แสดงเมื่อเข้าเงื่อนไข
            </v-btn>
            <v-btn value="advanced" size="small">
              <v-icon start size="16">mdi-code-tags</v-icon>กำหนดเอง
            </v-btn>
          </v-btn-toggle>

          <template v-if="vwMode === 'conditional'">
            <div v-if="vwConditions.length > 1" class="tq-vw-matchmode">
              <span class="tq-vw-matchmode-label">เมื่อมีหลายเงื่อนไข ให้ถือว่า</span>
              <v-btn-toggle
                data-testid="tq-item-vw-join"
                v-model="vwJoin"
                mandatory
                divided
                density="comfortable"
                variant="outlined"
                color="primary"
              >
                <v-btn value="AND" size="small">ต้องเข้าทุกเงื่อนไข</v-btn>
                <v-btn value="OR" size="small">เข้าอย่างน้อย 1 เงื่อนไข</v-btn>
              </v-btn-toggle>
            </div>

            <transition-group name="tq-vw-list" tag="div" class="tq-vw-list">
              <div v-for="(cond, idx) in vwConditions" :key="cond._k" class="tq-vw-item">
                <div v-if="idx > 0" class="tq-vw-join-sep" aria-hidden="true">
                  <span class="tq-vw-join-word">{{ joinWord }}</span>
                </div>
                <v-row dense align="center" class="tq-vw-row">
                  <v-col cols="12" md="5">
                    <v-autocomplete
                      :data-testid="`tq-item-vw-field-${idx}`"
                      :model-value="cond.field"
                      :items="conditionFieldSelectItems"
                      item-title="title"
                      item-value="value"
                      :return-object="false"
                      label="อ้างอิงค่าจาก Tax Rule / field *"
                      variant="outlined"
                      density="comfortable"
                      prepend-inner-icon="mdi-variable"
                      hide-details
                      @update:model-value="onConditionFieldSelect(idx, $event)"
                    />
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-select
                      :data-testid="`tq-item-vw-operator-${idx}`"
                      v-model="cond.operator"
                      :items="conditionOperators"
                      item-title="title"
                      item-value="value"
                      label="เงื่อนไข *"
                      variant="outlined"
                      density="comfortable"
                      hide-details
                    />
                  </v-col>
                  <v-col cols="10" md="3">
                    <v-combobox
                      v-if="cond.operator === 'in'"
                      :data-testid="`tq-item-vw-values-${idx}`"
                      v-model="cond.values"
                      :items="valueSuggestionsFor(cond.field)"
                      multiple
                      chips
                      closable-chips
                      :return-object="false"
                      label="ค่าที่ยอมรับ *"
                      variant="outlined"
                      density="comfortable"
                      hide-details
                    />
                    <v-combobox
                      v-else
                      :data-testid="`tq-item-vw-value-${idx}`"
                      v-model="cond.value"
                      :items="valueSuggestionsFor(cond.field)"
                      :return-object="false"
                      label="ค่าที่ต้องตรง *"
                      variant="outlined"
                      density="comfortable"
                      hide-details
                    />
                  </v-col>
                  <v-col cols="2" md="1" class="d-flex align-center justify-center">
                    <v-btn
                      size="small"
                      density="comfortable"
                      variant="text"
                      icon
                      :data-testid="`tq-item-vw-remove-${idx}`"
                      :disabled="vwConditions.length <= 1"
                      aria-label="ลบเงื่อนไข"
                      @click="removeCondition(idx)"
                    >
                      <v-icon size="18">mdi-close</v-icon>
                      <v-tooltip activator="parent" location="top">ลบ</v-tooltip>
                    </v-btn>
                  </v-col>
                </v-row>
              </div>
            </transition-group>

            <v-btn
              data-testid="tq-item-vw-add"
              size="small"
              variant="outlined"
              color="primary"
              class="tq-vw-add"
              @click="addCondition"
            >
              <v-icon start>mdi-plus</v-icon>
              เพิ่มเงื่อนไข
            </v-btn>

            <v-alert
              data-testid="tq-item-vw-preview"
              type="info"
              variant="tonal"
              density="compact"
              class="tq-vw-preview"
            >
              <div v-if="previewIsAlways" class="d-flex align-center flex-wrap ga-1">
                <v-icon size="16">mdi-pencil-outline</v-icon>
                <span>ยังตั้งเงื่อนไขไม่ครบ — คำถามนี้จะแสดงเสมอจนกว่าจะกรอกให้ครบ</span>
              </div>
              <template v-else>
                <div class="d-flex align-center flex-wrap ga-1">
                  <v-icon size="16">mdi-eye-check-outline</v-icon>
                  <span>แสดงคำถามนี้เมื่อ:</span>
                  <code class="tq-vw-code">{{ builtVisibleWhen }}</code>
                </div>
                <div class="tq-vw-preview-note">ถ้าไม่เข้าเงื่อนไข คำถามนี้จะถูกข้ามไปโดยอัตโนมัติ</div>
              </template>
            </v-alert>
          </template>

          <template v-else-if="vwMode === 'advanced'">
            <v-text-field
              data-testid="tq-item-visiblewhen"
              v-model="vwRaw"
              label="เงื่อนไขการแสดงคำถาม (expression)"
              placeholder="Always"
              variant="outlined"
              density="comfortable"
              prepend-inner-icon="mdi-eye-outline"
              :hint="visibleWhenHintText"
              persistent-hint
            />
            <v-alert
              v-if="advancedWarning"
              type="warning"
              variant="tonal"
              density="compact"
              class="mt-2"
            >
              {{ advancedWarning }}
            </v-alert>
          </template>

          <p v-else class="text-caption text-medium-emphasis mb-0">คำถามนี้จะแสดงเสมอ (ไม่มีเงื่อนไข)</p>

          <v-row class="mt-1">
            <v-col cols="12" class="d-flex align-center">
              <v-switch
                data-testid="tq-item-active"
                v-model="item.isActive"
                label="เปิดใช้งาน"
                color="primary"
                inset
                hide-details
              />
            </v-col>
          </v-row>
        </div>

        <!-- ── BLOCK: สิ่งที่ผู้ใช้เห็นบนมือถือ (ข้อความ/wording) ──────── -->
        <div class="tq-block">
          <h4 class="tq-block-title">สิ่งที่ผู้ใช้เห็นบนมือถือ</h4>
          <v-textarea
            data-testid="tq-item-questiontext"
            v-model="item.questionTextTh"
            label="ข้อความคำถาม (ภาษาไทย) — สิ่งที่ผู้ใช้เห็น"
            placeholder="เช่น ปีนี้คุณจ่ายเบี้ยประกันชีวิตของตัวเองไปเท่าไรคะ"
            variant="outlined"
            density="comfortable"
            rows="2"
            auto-grow
          />

          <v-text-field
            data-testid="tq-item-label"
            v-model="item.label"
            label="Label (สั้น ๆ สำหรับ admin) *"
            variant="outlined"
            density="comfortable"
            prepend-inner-icon="mdi-format-title"
            :rules="[rules.required]"
          />

          <v-textarea
            data-testid="tq-item-helptext"
            v-model="item.helpText"
            label="Help text (คำอธิบายเสริม)"
            variant="outlined"
            density="comfortable"
            rows="2"
            auto-grow
          />

          <!-- 🆕(rev2) answer-guidance bubble — "how to answer / examples" -->
          <v-textarea
            v-if="!isLayout && !isConfirm"
            data-testid="tq-item-answerguidance"
            v-model="item.answerGuidanceTh"
            label="คำแนะนำวิธีตอบ + ตัวอย่าง (answer guidance)"
            placeholder="เช่น ตอบเป็นยอดทั้งปี (1.2 ล้าน) หรือรายเดือน (เดือนละ 65,000) หรือเล่ารวม ๆ — AI จะช่วยสรุปเป็นรายปีให้"
            variant="outlined"
            density="comfortable"
            rows="2"
            auto-grow
            hint="บับเบิลแยกหลังข้อความคำถาม บอกผู้ใช้ว่าจะตอบแบบไหนได้บ้าง"
            persistent-hint
          />

          <!-- 🆕 ปุ่มตอบด่วน (quick reply) — แทนที่ "คำตอบที่แนะนำ" แบบพิมพ์เอง: admin เขียน prompt
               แล้วเปิด toggle; Tax Planning node จะให้ AI สร้างปุ่มตอนแชท (ปิด toggle = ข้ามการสร้างปุ่มไปเลย).
               ซ่อนสำหรับ LAYOUT / group / income classifier / confirm เช่นเดียวกับคำตอบแบบกดเลือกเดิม. -->
          <div v-if="showQuickTapAnswers" class="tq-options-block">
            <div class="tq-options-header">
              <span class="tq-label">ปุ่มตอบด่วน (quick reply)</span>
              <v-switch
                data-testid="tq-item-quickreply-enabled"
                v-model="item.quickReplyEnabled"
                label="เปิดสร้างปุ่มตอบด่วน"
                color="primary"
                inset
                hide-details
                density="compact"
              />
            </div>

            <v-textarea
              v-if="item.quickReplyEnabled"
              data-testid="tq-item-quickreply-prompt"
              v-model="item.quickReplyPrompt"
              label="Prompt สำหรับสร้างปุ่มตอบด่วน"
              placeholder="เช่น สร้างปุ่มคำตอบสั้น ๆ สำหรับสถานภาพสมรส เช่น โสด / สมรส / หย่าร้าง / หม้าย"
              variant="outlined"
              density="comfortable"
              rows="3"
              auto-grow
              hint="AI จะใช้ prompt นี้ + ข้อความคำถาม สร้างปุ่มตอบด่วนตอนแชท (เว้นว่างได้ = ใช้คำแนะนำมาตรฐาน)"
              persistent-hint
            />
            <p v-else class="text-caption text-medium-emphasis mb-0">
              ปิดอยู่ — คำถามนี้จะไม่มีปุ่มตอบด่วน (ผู้ใช้พิมพ์คำตอบเอง)
            </p>
          </div>
        </div>

        <!-- ── BLOCK 4: คำสั่ง AI เฉพาะคำถามนี้ (collapsible) ──────────
             Hidden for CONFIRM — its question/answer prompts are the primary authoring
             surface and are surfaced directly in the confirm editor block above (so
             they are not buried, and never double-bound to item.*SystemPrompt). -->
        <v-expansion-panels v-if="!isConfirm" variant="accordion" class="tq-prompt-panel">
          <v-expansion-panel>
            <v-expansion-panel-title>
              <v-icon size="18" class="mr-2">mdi-robot-outline</v-icon>
              คำสั่ง AI เฉพาะคำถามนี้ (ขั้นสูง)
            </v-expansion-panel-title>
            <v-expansion-panel-text>
              <v-textarea
                data-testid="tq-item-questionsystemprompt"
                v-model="item.questionSystemPrompt"
                label="System prompt — ตอนถาม (Question)"
                placeholder="คำสั่งเสริมให้ AI ตอน 'เรียบเรียงคำถาม' ของคำถามนี้ — เติมต่อจากระดับ Section/Node"
                variant="outlined"
                density="comfortable"
                rows="3"
                auto-grow
                hide-details
                class="mb-4"
              />
              <v-textarea
                data-testid="tq-item-answersystemprompt"
                v-model="item.answerSystemPrompt"
                label="System prompt — ตอนสกัดคำตอบ (Answer)"
                placeholder="คำสั่งเสริมให้ AI ตอน 'สกัดคำตอบ' ของคำถามนี้ — เติมต่อจากระดับ Section/Node"
                variant="outlined"
                density="comfortable"
                rows="3"
                auto-grow
                hide-details
                class="mb-4"
              />
              <v-autocomplete
                data-testid="tq-item-knowledge"
                v-model="item.knowledgeIds"
                :items="knowledgeCatalog"
                item-title="title"
                item-value="knowledgeCode"
                label="Knowledge เฉพาะคำถามนี้"
                placeholder="พิมพ์เพื่อค้นหา Knowledge"
                hint="ใช้เสริมจาก Knowledge ของ Section ตอนถาม/สกัด/จำแนกคำตอบของคำถามนี้ (รวมกันแบบ union)"
                persistent-hint
                variant="outlined"
                density="comfortable"
                prepend-inner-icon="mdi-book-open-variant"
                multiple
                chips
                closable-chips
                auto-select-first
                no-data-text="ไม่พบ Knowledge ที่ค้นหา"
              />
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-card-text>

      <v-card-actions class="pa-4">
        <v-spacer />
        <v-btn data-testid="tq-item-dialog-cancel" variant="outlined" @click="closeDialog">
          <v-icon start>mdi-close</v-icon>
          Cancel
        </v-btn>
        <v-btn data-testid="tq-item-dialog-save" color="primary" variant="elevated" @click="submit">
          <v-icon start>{{ isEditMode ? 'mdi-check' : 'mdi-playlist-plus' }}</v-icon>
          {{ isEditMode ? 'Apply changes' : 'Add to list' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- Inline "create a flag" panel — opened from a field picker's ＋ sentinel. Creates
       one boolean (ใช่/ไม่ใช่) Tax Rule via the append endpoint, then binds it back to the
       row/condition that requested it. No navigation away; the parent dialog stays open. -->
  <v-dialog
    :model-value="showCreateFlag"
    max-width="480px"
    persistent
    data-testid="tq-create-flag-dialog"
    @update:model-value="$event ? null : cancelCreateFlag()"
  >
    <v-card>
      <v-card-title class="bg-primary text-white d-flex align-center pa-4">
        <v-icon class="mr-2">mdi-flag-plus</v-icon>
        <span class="text-h6">สร้าง flag ใหม่ (ใช่/ไม่ใช่)</span>
      </v-card-title>
      <v-card-text class="pa-4 pt-0">
        <p class="text-caption text-medium-emphasis mb-4">
          flag คือ Tax Rule แบบตอบ ใช่/ไม่ใช่ ถูกเก็บในหมวด “ตัวควบคุมโฟลว์” (CTRL)
          และผูกกับคำถามนี้ทันทีโดยไม่ต้องไปหน้าแก้ Tax Rule
        </p>

        <v-alert
          v-if="flagError"
          type="error"
          variant="tonal"
          density="compact"
          class="mb-4"
          data-testid="tq-create-flag-error"
        >
          {{ flagError }}
        </v-alert>

        <v-text-field
          data-testid="tq-create-flag-label"
          :model-value="newFlag.label"
          label="ชื่อ flag (ภาษาไทย) *"
          placeholder="เช่น คู่สมรสมีเงินได้"
          variant="outlined"
          density="comfortable"
          autofocus
          prepend-inner-icon="mdi-format-title"
          @update:model-value="onFlagLabelInput"
        />
        <v-text-field
          data-testid="tq-create-flag-key"
          :model-value="newFlag.key"
          label="Key (สร้างอัตโนมัติ แก้ไขได้) *"
          placeholder="เช่น spouse_has_income"
          variant="outlined"
          density="comfortable"
          prepend-inner-icon="mdi-identifier"
          hint="อักษร a-z, 0-9 และ _ เท่านั้น — จะถูกเก็บเป็น UPPER_SNAKE"
          persistent-hint
          @update:model-value="onFlagKeyInput"
        />
        <div class="d-flex align-center mt-3">
          <v-chip size="small" color="primary" variant="tonal" prepend-icon="mdi-toggle-switch">
            ชนิด: boolean (ใช่/ไม่ใช่)
          </v-chip>
        </div>
      </v-card-text>
      <v-card-actions class="pa-4 pt-0">
        <v-spacer />
        <v-btn
          data-testid="tq-create-flag-cancel"
          variant="outlined"
          :disabled="creatingFlag"
          @click="cancelCreateFlag"
        >
          <v-icon start>mdi-close</v-icon>
          Cancel
        </v-btn>
        <v-btn
          data-testid="tq-create-flag-confirm"
          color="primary"
          variant="elevated"
          :loading="creatingFlag"
          @click="submitCreateFlag"
        >
          <v-icon start>mdi-check</v-icon>
          สร้างและผูก
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { taxConfigApi } from '../services/taxConfigApi';
import {
  taxQuestionStore,
  LAYOUT_INPUT_TYPES,
  PND_CODES,
  PND_LEGEND,
  VISIBLE_WHEN_HINTS,
  PERSONAL_INFO_FIELD_KEYS,
  SPECIAL_INPUT_FIELD_KEYS,
} from '../utils/taxQuestionStore';
import { VISIBLE_WHEN_OPERATORS, parseVisibleWhen, buildVisibleWhen, visibleWhenWarning } from '../utils/visibleWhen';

// Thai labels for the personal-info / special-input field keys shown in the
// visibility-condition field picker (e.g. "เพศ (sex)").
const FIELD_KEY_LABELS = {
  sex: 'เพศ',
  age: 'อายุ',
  marital_status: 'สถานะสมรส',
  spouse_info_status: 'สถานะข้อมูลคู่สมรส',
  date_of_birth: 'วันเกิด',
  nationality: 'สัญชาติ',
  tax_residency: 'พำนักในไทยครบ 180 วัน',
  withholding_tax: 'ภาษีหัก ณ ที่จ่าย',
  form_type: 'ประเภทแบบ (ภ.ง.ด.)',
  tax_year: 'ปีภาษี',
};

// Known finite value sets for personal-info / special-input field keys, so the
// guided VisibleWhen builder's VALUE side can offer real suggestions instead of a
// blank combobox. Advisory only — the value input stays free-text (values are
// consumed verbatim, case-insensitively, by the backend evaluator). Keys are the
// verbatim lower_snake fieldKeys.
const KNOWN_FIELD_VALUES = {
  marital_status: ['single', 'married'],
  spouse_info_status: ['known', 'unknown'],
  sex: ['male', 'female'],
  form_type: ['90', '91', '94'],
  tax_residency: ['yes', 'no'],
};

// 🆕 R2 — high-level question TYPE. Drives which blocks render + the emitted
// ItemKind. Single-field types (yes_no / amount / choice) materialize to a SCALAR
// item (RULE_INPUT top-level ruleCode, or PERSONAL_INFO/SPECIAL_INPUT top-level
// fieldKey) — NOT a GROUP_INPUT-with-1-field. group → GROUP_INPUT (boundFields),
// income_classifier → INCOME_CLASSIFIER, layout → LAYOUT.
const QUESTION_TYPE_OPTIONS = [
  { value: 'yes_no', title: 'ใช่ / ไม่ใช่' },
  { value: 'amount', title: 'จำนวนเงิน' },
  { value: 'choice', title: 'ตัวเลือก' },
  { value: 'group', title: 'กลุ่มหลาย field' },
  { value: 'income_classifier', title: 'จำแนกรายได้' },
  { value: 'confirm', title: 'จุดยืนยัน/สรุป (Confirm)' },
  { value: 'layout', title: 'หัวข้อ / แบนเนอร์' },
];
const QUESTION_TYPE_HINTS = {
  yes_no: 'คำถามตอบ ใช่/ไม่ใช่ — เก็บเป็น yes/no (ผูก 1 field)',
  amount: 'คำถามกรอกจำนวนเงิน / ตัวเลข (ผูก 1 field)',
  choice: 'คำถามเลือกจากตัวเลือก (ผูก 1 field)',
  group: 'คำถามเดียวเก็บหลาย field — ผู้ใช้ตอบรวม ระบบแยกให้เป็นราย field',
  income_classifier: 'ให้ AI จำแนกข้อความรายได้เป็นหลายประเภท (ม.40) แล้วตั้ง flag ต่อประเภท',
  confirm: 'จุดยืนยัน/สรุป — สรุปคำตอบที่เก็บมาแล้วถามผู้ใช้ให้ยืนยัน/แก้ไข (ไม่ผูกกฎภาษี/ข้อมูลส่วนตัว)',
  layout: 'หัวข้อ / แบนเนอร์ — ไม่เก็บคำตอบ (ใช้จัดวางเท่านั้น)',
};
// Single-field TYPES map to a scalar item; the ItemKind is decided by WHAT is bound.
const SINGLE_FIELD_TYPES = ['yes_no', 'amount', 'choice'];
// Advisory default answer TYPE for a bound personal/special field key when the item
// carries no explicit answerType/controlType — improves the derived TYPE for legacy
// field-key bindings on open. Never changes the runtime/serialization contract.
const FIELD_KEY_DEFAULT_TYPE = {
  sex: 'choice',
  marital_status: 'choice',
  spouse_info_status: 'choice',
  date_of_birth: 'choice',
  nationality: 'choice',
  form_type: 'choice',
  tax_residency: 'yes_no',
  age: 'amount',
  withholding_tax: 'amount',
  tax_year: 'amount',
};

// Sentinel option injected at the top of every field picker (bound-field rows +
// visibility-condition rows). Selecting it does NOT bind a field — it opens the
// inline "create a flag" panel. The value is namespaced so it can never collide
// with a real ruleCode/fieldKey.
const CREATE_FLAG_VALUE = '__tq_create_flag__';
const CREATE_FLAG_TITLE = '＋ สร้าง flag ใหม่ (ใช่/ไม่ใช่)';

// Stable per-row key for the visibility-condition list, so add/remove animate the
// right row (index keys would misfire on removal from the middle).
let condKeySeq = 0;
function nextCondKey() {
  condKeySeq += 1;
  return condKeySeq;
}

export default {
  name: 'TaxQuestionItemDialog',
  props: {
    modelValue: { type: Boolean, default: false },
    initialItem: { type: Object, default: null },
    configVersionId: { type: Number, default: null },
    availableItems: { type: Array, default: () => [] },
    // 🆕 R6 — the builder's section list ({ code, label, … }) so a question can be
    // re-filed into another Section from the dialog.
    sections: { type: Array, default: () => [] },
    // 🆕 knowledge catalog ({ knowledgeCode, title }) from MetaDoc, passed down from the parent
    // so a question can bind its own knowledge (union'd with its section's at chat time).
    knowledgeCatalog: { type: Array, default: () => [] },
  },
  emits: ['update:modelValue', 'submit'],
  data() {
    return {
      item: taxQuestionStore.createBlankItem(),
      submitAttempted: false,
      rules: {
        required: (value) => (value !== null && value !== undefined && String(value).trim() !== '') || 'Required',
      },
      ruleOptions: [],
      loadingRules: false,
      // Inline "create a flag" panel state (opened from a field picker's sentinel).
      showCreateFlag: false,
      creatingFlag: false,
      flagError: '',
      // { source: 'bound' | 'condition', idx } — which picker to bind the new flag back to.
      flagCtx: null,
      newFlag: { label: '', key: '', keyEdited: false },
      pndCodes: PND_CODES,
      pndLegend: PND_LEGEND,
      visibleWhenHintText: `ใส่ "Always" เพื่อแสดงเสมอ · ตัวอย่าง: ${VISIBLE_WHEN_HINTS.slice(1).join(' · ')}`,
      vwMode: 'always',
      vwConditions: [],
      vwJoin: 'AND',
      vwRaw: 'Always',
      conditionOperators: VISIBLE_WHEN_OPERATORS,
      // 🆕 R2 — high-level question TYPE (drives blocks + emitted ItemKind).
      qType: 'yes_no',
      // 🆕 R6 — bumped to force the controlled type <v-select> to snap back to qType
      // when a down-conversion (multi-field group → single-field) is cancelled.
      typeSelectKey: 0,
      // Whether qType was auto-derived (vs picked by the admin) — lets the async
      // rule-type refine run without clobbering an explicit choice.
      qTypeAuto: true,
      // answerType loaded from the item, used to preserve number/checkbox_group
      // round-trips that the simplified 3-item single-field picker can't express.
      loadedAnswerType: '',
      questionTypeOptions: QUESTION_TYPE_OPTIONS,
    };
  },
  computed: {
    isEditMode() {
      return Boolean(this.initialItem && this.initialItem.questionItemID != null);
    },
    // 🆕 R2 — rendering is now driven by the high-level question TYPE (qType), not
    // item.itemKind. The itemKind is materialized from qType + the binding on
    // submit (see materializeItemForSubmit), so these stay in sync with the picker.
    isLayout() {
      return this.qType === 'layout';
    },
    // 🆕 Group question — one question captures MANY fields (mixed Tax Rule +
    // personal + special), each with its own "required" toggle. Multi-row editor.
    isGroupInput() {
      return this.qType === 'group';
    },
    // 🆕 Income classifier — one item carries a catalog of income categories
    // (§40 types) that the LLM uses to classify a free-text income message into
    // yes/no flags at runtime. Authored as a list of { code, labelTh, flagKey,
    // keywords[] } rows.
    isIncomeClassifier() {
      return this.qType === 'income_classifier';
    },
    // 🆕 Confirm checkpoint — one item pauses the walk to summarize the collected
    // answers and gate on ยืนยัน/แก้ไข. Binds NO tax rule / personal field; carries a
    // stable confirm key (fieldKey) + a question prompt (how to summarize + ask) and
    // an answer prompt (how to read the reply). No widget, no options.
    isConfirm() {
      return this.qType === 'confirm';
    },
    // 🆕 R2 — single-field question (ใช่/ไม่ใช่ · จำนวนเงิน · ตัวเลือก): binds exactly
    // ONE field and materializes to a SCALAR item (RULE_INPUT / PERSONAL_INFO /
    // SPECIAL_INPUT) on submit.
    isSingleField() {
      return SINGLE_FIELD_TYPES.includes(this.qType);
    },
    // The single binding's identifier (ruleCode || fieldKey) for the single-field
    // picker's model-value. The binding lives in boundFields[0] while editing.
    singleBindValue() {
      const bf = (this.item.boundFields || [])[0];
      return bf ? (bf.ruleCode || bf.fieldKey || null) : null;
    },
    questionTypeHint() {
      return QUESTION_TYPE_HINTS[this.qType] || '';
    },
    // 🆕 The quick-reply block only makes sense for single-field widget questions. Hide it for
    // LAYOUT (no answer) and for the free-text group + income-classifier + confirm types, where
    // the user types freely and the LLM splits/classifies (same gating as the old answerOptions list).
    showQuickTapAnswers() {
      return !this.isLayout && !this.isGroupInput && !this.isIncomeClassifier && !this.isConfirm;
    },
    // 🆕 R6 — Section (groupCode) re-file options, from the builder's section list.
    // The synthetic GENERAL bucket maps to an empty value (= no section).
    sectionOptions() {
      return (Array.isArray(this.sections) ? this.sections : [])
        .map((s) => {
          const code = String((s && s.code) || '').trim().toUpperCase();
          if (!code) return null;
          return {
            value: code === 'GENERAL' ? '' : code,
            title: (s && s.label) || code,
          };
        })
        .filter(Boolean);
    },
    // Current section value for the picker (UPPER groupCode, or '' for no section).
    currentSectionValue() {
      return String(this.item.groupCode || '').trim().toUpperCase();
    },
    inputTypeOptions() {
      return LAYOUT_INPUT_TYPES;
    },
    builderState() {
      return {
        mode: this.vwMode,
        join: this.vwJoin,
        conditions: this.vwConditions,
        raw: this.vwRaw,
      };
    },
    builtVisibleWhen() {
      return buildVisibleWhen(this.builderState);
    },
    advancedWarning() {
      return visibleWhenWarning(this.vwRaw, this.knownFieldKeyPool);
    },
    // The pool of field keys the guided picker knows about (Tax Rule codes +
    // personal + special + sibling fields). Passed to visibleWhenWarning so an
    // advanced/raw expression gets a SOFT, visible warning when it references a
    // field key outside this pool. Advisory only — never blocks save, never
    // changes serialized output (fail-open runtime, in sync with the backend
    // evaluator which treats an unknown/unanswered key as visible).
    knownFieldKeyPool() {
      return this.conditionFieldFlat.map((o) => o && o.value).filter(Boolean);
    },
    // Thai connective shown between condition rows so the AND/OR relationship is
    // visible inline, not just in the toggle above.
    joinWord() {
      return this.vwJoin === 'OR' ? 'หรือ' : 'และ';
    },
    // An incomplete conditional serializes to "Always" — surface that as a
    // distinct not-yet-set state instead of showing a contradictory expression.
    previewIsAlways() {
      return this.builtVisibleWhen === 'Always';
    },
    // Grouped field-source items for each per-row <v-select>. Order: Tax Rule →
    // ข้อมูลส่วนตัว → ช่องพิเศษ → Field จากคำถามอื่น. Deduped by identifier across
    // groups (first wins). Vuetify renders {type:'subheader'} / {type:'divider'}
    // entries as non-selectable; a group's header/divider is only added when the
    // group has ≥1 real option.
    conditionFieldSelectItems() {
      // Groups a/b/c (Tax Rule → personal → special) come from the shared builder;
      // group d (sibling questions) is appended here only for the condition picker.
      const { out, used } = this.buildBaseFieldGroups();

      // d. Field จากคำถามอื่น — sibling identifiers not already covered above.
      const siblingGroup = [];
      (Array.isArray(this.availableItems) ? this.availableItems : []).forEach((it) => {
        if (!it) return;
        if (it._uid === this.item._uid) return;
        if (it.itemKind === 'LAYOUT') return;
        // 🆕 A GROUP_INPUT sibling captures MANY fields — expose EACH bound field's
        // key (ruleCode||fieldKey) as its own selectable option so other questions'
        // visibility conditions can reference a field captured inside a group.
        if (it.itemKind === 'GROUP_INPUT') {
          (Array.isArray(it.boundFields) ? it.boundFields : []).forEach((bf) => {
            const bfId = String((bf && (bf.ruleCode || bf.fieldKey)) || '').trim();
            if (!bfId || used.has(bfId)) return;
            used.add(bfId);
            siblingGroup.push({
              value: bfId,
              title: `${(bf && bf.label) || bfId} (${bfId})`,
              kind: 'SIBLING',
              options: [],
            });
          });
          return;
        }
        // 🆕 An INCOME_CLASSIFIER sibling writes a yes/no flag PER income category at
        // runtime (FieldKey = flagKey, e.g. type_40_1). Expose EACH flagKey as its own
        // selectable field so income questions can author `type_40_x = yes` without
        // typing it by hand. Value options yes/no feed the value-suggestion picker.
        if (it.itemKind === 'INCOME_CLASSIFIER') {
          (Array.isArray(it.incomeCategories) ? it.incomeCategories : []).forEach((cat) => {
            const flagId = String((cat && cat.flagKey) || '').trim();
            if (!flagId || used.has(flagId)) return;
            used.add(flagId);
            siblingGroup.push({
              value: flagId,
              title: `${(cat && cat.labelTh) || flagId} (${flagId})`,
              kind: 'SIBLING',
              options: ['yes', 'no'],
            });
          });
          return;
        }
        const id = String((it.itemKind === 'RULE_INPUT' ? it.ruleCode : it.fieldKey) || '').trim();
        if (!id || used.has(id)) return;
        used.add(id);
        siblingGroup.push({
          value: id,
          title: `${it.label || id} (${id})`,
          kind: 'SIBLING',
          options: Array.isArray(it.answerOptions) ? it.answerOptions.filter(Boolean) : [],
        });
      });
      if (siblingGroup.length) {
        if (out.length) out.push({ type: 'divider' });
        out.push({ type: 'subheader', title: 'Field จากคำถามอื่น' });
        out.push(...siblingGroup);
      }

      return this.withCreateFlagSentinel(out);
    },
    // 🆕 Selectable field pool for the GROUP_INPUT bound-field rows — the SAME
    // sources as conditionFieldSelectItems groups a/b/c (Tax Rule + ข้อมูลส่วนตัว +
    // ช่องพิเศษ) but EXCLUDING sibling questions (group d). Each real option carries
    // { value, title, kind } so a picked row can set fieldKind + ruleCode/fieldKey.
    boundFieldPoolItems() {
      return this.withCreateFlagSentinel(this.buildBaseFieldGroups().out);
    },
    // Flat list of the real (selectable) options — no subheaders/dividers and no the
    // ＋ create-flag sentinel — used by valueSuggestionsFor() / knownFieldKeyPool.
    conditionFieldFlat() {
      return this.conditionFieldSelectItems.filter(
        (o) => o && !o.type && o.kind !== 'CREATE_FLAG',
      );
    },
    validationErrors() {
      const e = [];
      const it = this.item;
      if (!String(it.label || '').trim()) e.push('ต้องกรอก label');
      if (this.isGroupInput) {
        const rows = Array.isArray(it.boundFields) ? it.boundFields : [];
        const resolved = rows.filter(
          (bf) => bf && (String(bf.ruleCode || '').trim() || String(bf.fieldKey || '').trim()),
        );
        if (!resolved.length) e.push('กลุ่มหลาย field ต้องผูกอย่างน้อย 1 field');
      } else if (this.isSingleField) {
        const bf = (Array.isArray(it.boundFields) ? it.boundFields : [])[0];
        const bound = bf && (String(bf.ruleCode || '').trim() || String(bf.fieldKey || '').trim());
        if (!bound) e.push('ต้องเลือก Tax Rule / field ที่คำถามนี้ผูก');
      }
      if (this.isIncomeClassifier) {
        const cats = Array.isArray(it.incomeCategories) ? it.incomeCategories : [];
        const complete = cats.filter(
          (c) => c
            && String(c.code || '').trim()
            && String(c.flagKey || '').trim()
            && String(c.labelTh || '').trim(),
        );
        if (!complete.length) e.push('INCOME_CLASSIFIER ต้องมีประเภทรายได้ที่กรอกรหัส + flag + ชื่อ ครบอย่างน้อย 1 ประเภท');
      }
      if (this.isConfirm) {
        if (!String(it.fieldKey || '').trim()) e.push('จุดยืนยันต้องมี confirm key (fieldKey)');
        if (!String(it.questionTextTh || '').trim()) e.push('จุดยืนยันต้องมีข้อความสรุป/เกริ่น (questionTextTh)');
      }
      if (this.isLayout && !LAYOUT_INPUT_TYPES.includes(it.inputType)) {
        e.push('LAYOUT ต้องใช้ input type เป็น section_header หรือ info_banner');
      }
      if (this.vwMode === 'conditional') {
        const rows = this.vwConditions || [];
        const complete = rows.filter((c) => {
          if (!String(c.field || '').trim()) return false;
          if (c.operator === 'in') return (c.values || []).some((v) => String(v).trim());
          return !!String(c.value || '').trim();
        });
        if (!complete.length) e.push('เงื่อนไขการแสดง: ต้องมีเงื่อนไขที่กรอกครบอย่างน้อย 1 ข้อ');
        if (this.builtVisibleWhen.length > 200) e.push('เงื่อนไขการแสดง: ยาวเกินไป (จำกัด 200 ตัวอักษร) — ลดจำนวนเงื่อนไขลง');
      }
      return e;
    },
  },
  watch: {
    modelValue(open) {
      if (open) {
        this.resetForm();
        this.loadRules();
      }
    },
    vwMode(mode) {
      if (mode !== 'advanced') return;
      const cur = String(this.vwRaw || '').trim().toLowerCase();
      if (cur && cur !== 'always') return; // don't clobber an existing raw expr
      this.vwRaw = buildVisibleWhen({
        mode: 'conditional',
        join: this.vwJoin,
        conditions: this.vwConditions,
      });
    },
  },
  methods: {
    resetForm() {
      this.item = this.initialItem
        ? JSON.parse(JSON.stringify(this.initialItem))
        : taxQuestionStore.createBlankItem();
      if (this.item._uid == null) this.item._uid = taxQuestionStore.createBlankItem()._uid;
      if (!Array.isArray(this.item.answerOptions)) this.item.answerOptions = [];
      if (!Array.isArray(this.item.pndCodes)) this.item.pndCodes = [];
      if (!Array.isArray(this.item.boundFields)) this.item.boundFields = [];
      if (!Array.isArray(this.item.incomeCategories)) this.item.incomeCategories = [];
      if (!Array.isArray(this.item.knowledgeIds)) this.item.knowledgeIds = [];
      // 🆕 quick-reply scalars — default for legacy items cloned without these fields.
      if (typeof this.item.quickReplyEnabled !== 'boolean') this.item.quickReplyEnabled = false;
      if (this.item.quickReplyPrompt == null) this.item.quickReplyPrompt = '';
      // Remember the loaded answerType so a single-field save can preserve the
      // number / checkbox_group variants the simplified TYPE picker can't express.
      this.loadedAnswerType = String(this.item.answerType || '').trim().toLowerCase();
      // 🆕 R2 — DERIVE the high-level question TYPE from the loaded item (no blanket
      // GROUP_INPUT coercion). The TYPE then drives which blocks render + the emitted
      // ItemKind (materialized on submit). A brand-new blank defaults to yes_no.
      this.qType = this.deriveQTypeInitial(this.item);
      this.qTypeAuto = true;
      // Shape the working item for the derived TYPE (migrate a scalar single binding
      // into boundFields[0] for single-field types, ensure a group row, section_header
      // for layout, …) WITHOUT dropping any binding.
      this.applyTypeToItem(this.qType);
      const vw = parseVisibleWhen(this.item.visibleWhen);
      this.vwMode = vw.mode;
      this.vwJoin = vw.join || 'AND';
      this.vwRaw = vw.mode === 'advanced' ? vw.raw : (this.item.visibleWhen || 'Always');
      this.vwConditions = (vw.conditions && vw.conditions.length)
        ? vw.conditions.map((c) => ({
            _k: nextCondKey(),
            field: c.field,
            operator: c.operator || '=',
            value: c.value || '',
            values: Array.isArray(c.values) ? c.values.slice() : [],
          }))
        : [{ _k: nextCondKey(), field: null, operator: '=', value: '', values: [] }];
      this.submitAttempted = false;
    },
    // ── 🆕 R2 — question TYPE derivation + materialization ─────────────────────
    // Derive the high-level TYPE from a loaded item using only synchronously
    // available signals (itemKind, bound-field count, answerType/controlType,
    // answerOptions, field-key hints). Rule-type refinement (boolean→yes_no,
    // number→amount) happens later once the rule set loads (refineQTypeFromRules).
    deriveQTypeInitial(item) {
      const kind = String(item.itemKind || '').trim().toUpperCase();
      if (kind === 'LAYOUT') return 'layout';
      if (kind === 'INCOME_CLASSIFIER') return 'income_classifier';
      if (kind === 'CONFIRM') return 'confirm';
      const boundCount = (Array.isArray(item.boundFields) ? item.boundFields : [])
        .filter((bf) => bf && (String(bf.ruleCode || '').trim() || String(bf.fieldKey || '').trim()))
        .length;
      // A GROUP_INPUT with >1 bound field is a real group; with ≤1 it collapses to a
      // single-field type (its single binding is preserved — see applyTypeToItem).
      if (kind === 'GROUP_INPUT' && boundCount > 1) return 'group';
      const hasBinding = boundCount > 0
        || String(item.ruleCode || '').trim()
        || String(item.fieldKey || '').trim();
      // Brand-new blank (no id, nothing bound) → sensible default.
      if (item.questionItemID == null && !hasBinding) return 'yes_no';
      return this.deriveSingleFieldTypeFromSignals(item);
    },
    // Map an item's stored widget signals → a single-field TYPE (yes_no/amount/choice).
    deriveSingleFieldTypeFromSignals(item) {
      const at = String(item.answerType || '').trim().toLowerCase();
      if (at === 'yes_no' || at === 'amount' || at === 'choice') return at;
      if (at === 'number') return 'amount';
      if (at === 'checkbox_group') return 'choice';
      const ct = String(item.controlType || '').trim().toLowerCase();
      if (ct === 'amount_input' || ct === 'number_input') return 'amount';
      if (ct === 'option_cards' || ct === 'checkbox_group') return 'choice';
      const hasOptions = Array.isArray(item.answerOptions)
        && item.answerOptions.filter((o) => String(o || '').trim()).length > 0;
      if (hasOptions) return 'choice';
      // Field-key intrinsic hint (personal/special fields carry a natural type).
      const fk = String((Array.isArray(item.boundFields) && item.boundFields[0]
        ? item.boundFields[0].fieldKey : item.fieldKey) || '').trim();
      if (fk && FIELD_KEY_DEFAULT_TYPE[fk]) return FIELD_KEY_DEFAULT_TYPE[fk];
      return 'yes_no';
    },
    ruleTypeFor(code) {
      const c = String(code || '').trim().toUpperCase();
      if (!c) return '';
      const opt = (this.ruleOptions || []).find((o) => String(o.value || '').trim().toUpperCase() === c);
      return opt && opt.rule ? String(opt.rule.type || '').trim().toLowerCase() : '';
    },
    ruleHasOptions(code) {
      const c = String(code || '').trim().toUpperCase();
      if (!c) return false;
      const opt = (this.ruleOptions || []).find((o) => String(o.value || '').trim().toUpperCase() === c);
      return Boolean(opt && opt.rule && Array.isArray(opt.rule.options) && opt.rule.options.length);
    },
    // Once the rule set has loaded, refine an auto-derived single-field TYPE from the
    // bound rule's real `type` (boolean→yes_no, number→amount, has options→choice).
    // Skips if the admin already picked a TYPE by hand (qTypeAuto false).
    refineQTypeFromRules() {
      if (!this.qTypeAuto || !this.isSingleField) return;
      const bf = (this.item.boundFields || [])[0];
      if (!bf || String(bf.fieldKind || '').toUpperCase() !== 'RULE_INPUT') return;
      const code = String(bf.ruleCode || '').trim().toUpperCase();
      if (!code) return;
      const t = this.ruleTypeFor(code);
      let next = null;
      if (t === 'boolean') next = 'yes_no';
      else if (t === 'number') next = 'amount';
      else if (this.ruleHasOptions(code)) next = 'choice';
      if (next && next !== this.qType) {
        this.qType = next;
        this.applyTypeToItem(next);
      }
    },
    // Admin picked a TYPE from the selector — switch the working item's shape.
    // 🆕 R6 — DOWN-CONVERSION GUARD: converting a multi-field group to a single-field
    // type keeps only the first bound field and silently drops the rest on submit;
    // confirm the loss first, and revert the picker if the admin cancels.
    onTypeChange(next) {
      if (!next || next === this.qType) return;
      // Down-conversion guard: leaving a multi-field group drops bindings. Single-field
      // types keep the FIRST binding; income_classifier / layout keep NONE. Warn either way.
      if (this.qType === 'group' && next !== 'group') {
        const resolved = (Array.isArray(this.item.boundFields) ? this.item.boundFields : [])
          .filter((bf) => bf && (String(bf.ruleCode || '').trim() || String(bf.fieldKey || '').trim()));
        const toSingle = SINGLE_FIELD_TYPES.includes(next);
        const drop = toSingle ? resolved.length - 1 : resolved.length;
        if (drop > 0) {
          const msg = toSingle
            ? `จะเก็บเฉพาะ field แรก, อีก ${drop} field จะถูกลบ`
            : `การเปลี่ยนชนิดนี้จะลบ field ที่ผูกไว้ทั้งหมด ${drop} field`;
          if (!window.confirm(msg)) {
            // Cancelled — snap the controlled <v-select> back to the current type.
            this.typeSelectKey += 1;
            return;
          }
          // Confirmed — single-field keeps the first binding; others clear on materialize.
          if (toSingle) this.item.boundFields = [resolved[0]];
        }
      }
      this.qType = next;
      this.qTypeAuto = false;
      this.applyTypeToItem(next);
    },
    // 🆕 R6 — re-file the question into another Section (or none). The move is applied
    // when the builder receives the submitted item (its groupCode regroups the list).
    onSectionChange(value) {
      const code = String(value == null ? '' : value).trim().toUpperCase();
      this.item.groupCode = code || null;
    },
    // Shape item to a TYPE without dropping bindings. For single-field types it keeps
    // the binding in boundFields[0]; the scalar itemKind/ruleCode/fieldKey are only
    // materialized on submit (materializeItemForSubmit).
    applyTypeToItem(t) {
      if (!Array.isArray(this.item.boundFields)) this.item.boundFields = [];
      if (!Array.isArray(this.item.incomeCategories)) this.item.incomeCategories = [];
      if (t === 'layout') {
        this.item.itemKind = 'LAYOUT';
        if (!LAYOUT_INPUT_TYPES.includes(this.item.inputType)) this.item.inputType = 'section_header';
        this.item.answerType = null;
        this.item.controlType = null;
        this.item.showRuleCap = false;
        return;
      }
      if (t === 'income_classifier') {
        this.item.itemKind = 'INCOME_CLASSIFIER';
        this.item.answerType = null;
        this.item.controlType = null;
        this.item.showRuleCap = false;
        return;
      }
      // 🆕 CONFIRM — clear every binding (no rule / bound / income); keep the confirm
      // key (fieldKey) + prompt fields so the editor block has something to bind to.
      if (t === 'confirm') {
        this.item.itemKind = 'CONFIRM';
        this.item.ruleCode = null;
        this.item.boundFields = [];
        this.item.incomeCategories = [];
        this.item.answerType = null;
        this.item.controlType = null;
        this.item.showRuleCap = false;
        if (this.item.fieldKey == null) this.item.fieldKey = '';
        if (this.item.questionSystemPrompt == null) this.item.questionSystemPrompt = '';
        if (this.item.answerSystemPrompt == null) this.item.answerSystemPrompt = '';
        return;
      }
      if (t === 'group') {
        this.item.itemKind = 'GROUP_INPUT';
        this.item.answerType = null;
        this.item.showRuleCap = false;
        if (this.item.controlType !== 'chat_prompt') this.item.controlType = 'text_input';
        // Fold a scalar single binding into a group row so nothing is lost; ensure a
        // blank row exists so the admin sees a row to fill.
        this.ensureBindingRowFromScalar();
        if (!this.item.boundFields.length) this.addBoundField();
        return;
      }
      // single-field: yes_no | amount | choice
      this.ensureBindingRowFromScalar();
      if (!this.item.boundFields.length) this.addBoundField();
      this.applySingleFieldWidget();
    },
    // Set answerType/controlType/inputType from the current single-field TYPE,
    // preserving number / checkbox_group variants the picker can't express when the
    // TYPE was auto-derived (qTypeAuto) rather than picked by hand.
    applySingleFieldWidget() {
      let at = this.qType;
      if (this.qTypeAuto) {
        if (at === 'amount' && this.loadedAnswerType === 'number') at = 'number';
        else if (at === 'choice' && this.loadedAnswerType === 'checkbox_group') at = 'checkbox_group';
      }
      this.item.answerType = at;
      this.item.controlType = taxQuestionStore.controlTypeForAnswer(at);
      this.item.inputType = taxQuestionStore.inputTypeForAnswer(at);
    },
    // If the item is a scalar (top-level ruleCode/fieldKey) but has no bound-field
    // row yet, seed boundFields[0] from it so the single/group editors show it.
    ensureBindingRowFromScalar() {
      if (this.item.boundFields.length) return;
      const rc = String(this.item.ruleCode || '').trim();
      const fk = String(this.item.fieldKey || '').trim();
      if (!rc && !fk) return;
      const kind = String(this.item.itemKind || '').trim().toUpperCase();
      let fieldKind;
      if (rc) fieldKind = 'RULE_INPUT';
      else if (kind === 'SPECIAL_INPUT' || kind === 'PERSONAL_INFO') fieldKind = kind;
      else fieldKind = SPECIAL_INPUT_FIELD_KEYS.includes(fk) ? 'SPECIAL_INPUT' : 'PERSONAL_INFO';
      this.item.boundFields = [{
        fieldKind,
        ruleCode: rc ? rc.toUpperCase() : null,
        fieldKey: rc ? null : fk,
        label: this.item.label || rc || fk || '',
        isRequired: !!this.item.isRequired,
      }];
    },
    addCondition() {
      this.vwConditions.push({ _k: nextCondKey(), field: null, operator: '=', value: '', values: [] });
    },
    removeCondition(idx) {
      this.vwConditions.splice(idx, 1);
      if (!this.vwConditions.length) this.addCondition();
    },
    // Value suggestions for a per-row value combobox: a sibling field's
    // answerOptions (empty for rules / personal / special fields).
    valueSuggestionsFor(field) {
      const id = String(field == null ? '' : field).trim();
      if (!id) return [];
      const opt = this.conditionFieldFlat.find((o) => o.value === id);
      return opt && Array.isArray(opt.options) ? opt.options : [];
    },
    async loadRules() {
      if (this.configVersionId == null) {
        this.ruleOptions = [];
        return;
      }
      this.loadingRules = true;
      try {
        const rules = await taxConfigApi.getRules(this.configVersionId, null, true);
        this.ruleOptions = (rules || []).map((r) => ({
          value: r.ruleCode,
          label: `${r.ruleCode} — ${r.label || ''}`.trim(),
          rule: r,
        }));
        // 🆕 R2 — now that rule types are known, refine an auto-derived single-field
        // TYPE from the bound rule's real type (boolean→yes_no, number→amount, …).
        this.refineQTypeFromRules();
      } catch {
        this.ruleOptions = [];
      } finally {
        this.loadingRules = false;
      }
    },
    // 🆕 Shared builder for the field pool used by both the visibility-condition
    // picker (conditionFieldSelectItems) and the GROUP_INPUT bound-field rows
    // (boundFieldPoolItems). Returns groups a/b/c (Tax Rule → ข้อมูลส่วนตัว →
    // ช่องพิเศษ) as Vuetify list items (subheaders/dividers are non-selectable) plus
    // the `used` id-set so a caller can dedupe additional groups (e.g. siblings).
    // NOTE: a fresh set/array per call — never a cached computed — so callers that
    // mutate `used` (conditionFieldSelectItems) don't poison later evaluations.
    buildBaseFieldGroups() {
      const used = new Set();
      const out = [];

      // a. Tax Rule — all rules of the config version.
      const ruleGroup = [];
      (Array.isArray(this.ruleOptions) ? this.ruleOptions : []).forEach((o) => {
        const id = String((o && o.value) || '').trim();
        if (!id || used.has(id)) return;
        used.add(id);
        // A boolean Tax Rule captures a yes/no answer → offer [yes, no] on the
        // VALUE side (derived from the rule's `type`, the same source of truth as
        // the answer-kind chip). Non-boolean rules have no finite value set.
        const ruleType = String((o.rule && o.rule.type) || '').trim().toLowerCase();
        ruleGroup.push({
          value: id,
          title: o.label || id,
          kind: 'RULE_INPUT',
          options: ruleType === 'boolean' ? ['yes', 'no'] : [],
        });
      });
      if (ruleGroup.length) {
        out.push({ type: 'subheader', title: 'Tax Rule' });
        out.push(...ruleGroup);
      }

      // b. ข้อมูลส่วนตัว — personal-info field keys.
      const personalGroup = [];
      PERSONAL_INFO_FIELD_KEYS.forEach((k) => {
        const id = String(k || '').trim();
        if (!id || used.has(id)) return;
        used.add(id);
        personalGroup.push({ value: id, title: `${FIELD_KEY_LABELS[id] || id} (${id})`, kind: 'PERSONAL_INFO', options: (KNOWN_FIELD_VALUES[id] || []).slice() });
      });
      if (personalGroup.length) {
        if (out.length) out.push({ type: 'divider' });
        out.push({ type: 'subheader', title: 'ข้อมูลส่วนตัว' });
        out.push(...personalGroup);
      }

      // c. ช่องพิเศษ — special-input field keys.
      const specialGroup = [];
      SPECIAL_INPUT_FIELD_KEYS.forEach((k) => {
        const id = String(k || '').trim();
        if (!id || used.has(id)) return;
        used.add(id);
        specialGroup.push({ value: id, title: `${FIELD_KEY_LABELS[id] || id} (${id})`, kind: 'SPECIAL_INPUT', options: (KNOWN_FIELD_VALUES[id] || []).slice() });
      });
      if (specialGroup.length) {
        if (out.length) out.push({ type: 'divider' });
        out.push({ type: 'subheader', title: 'ช่องพิเศษ' });
        out.push(...specialGroup);
      }

      return { out, used };
    },
    // 🆕 GROUP_INPUT bound-field row helpers (mirror the answerOptions list). A row
    // shape is { fieldKind, ruleCode, fieldKey, label, isRequired } — only one of
    // ruleCode/fieldKey is set, per fieldKind. fieldKey stays verbatim (never
    // uppercased); buildItemRequest applies the naming canon on save.
    addBoundField() {
      if (!Array.isArray(this.item.boundFields)) this.item.boundFields = [];
      this.item.boundFields.push({ fieldKind: 'RULE_INPUT', ruleCode: null, fieldKey: null, label: '', isRequired: true });
    },
    removeBoundField(idx) {
      if (!Array.isArray(this.item.boundFields)) { this.item.boundFields = []; return; }
      this.item.boundFields.splice(idx, 1);
    },
    moveBoundField(idx, dir) {
      if (!Array.isArray(this.item.boundFields)) { this.item.boundFields = []; return; }
      const target = idx + dir;
      if (target < 0 || target >= this.item.boundFields.length) return;
      const rows = this.item.boundFields;
      [rows[idx], rows[target]] = [rows[target], rows[idx]];
    },
    // Resolve the picked pool option → set the row's fieldKind + ruleCode/fieldKey +
    // label. Rule → ruleCode = id, fieldKey = null; personal/special → fieldKey = id
    // (verbatim), ruleCode = null. An unknown/cleared value blanks the target.
    setBoundFieldTarget(idx, value) {
      if (!Array.isArray(this.item.boundFields)) this.item.boundFields = [];
      const row = this.item.boundFields[idx];
      if (!row) return;
      const id = value == null ? '' : String(value).trim();
      const opt = this.boundFieldPoolItems.find((o) => o && !o.type && o.value === id);
      if (!opt) {
        row.ruleCode = null;
        row.fieldKey = null;
        return;
      }
      const isRuleBound = opt.kind === 'RULE_INPUT';
      row.fieldKind = opt.kind;
      row.ruleCode = isRuleBound ? id : null;
      row.fieldKey = isRuleBound ? null : id;
      row.label = opt.title || row.label || '';
    },
    setBoundFieldRequired(idx, value) {
      if (!Array.isArray(this.item.boundFields)) this.item.boundFields = [];
      const row = this.item.boundFields[idx];
      if (!row) return;
      row.isRequired = !!value;
    },
    // Prepend the ＋ "create a flag" sentinel (and a divider when there are real
    // options) to a field-picker item list. Selecting it is intercepted by
    // onBoundFieldSelect / onConditionFieldSelect and opens the inline create panel.
    withCreateFlagSentinel(list) {
      const items = Array.isArray(list) ? list : [];
      const sentinel = { value: CREATE_FLAG_VALUE, title: CREATE_FLAG_TITLE, kind: 'CREATE_FLAG' };
      return items.length ? [sentinel, { type: 'divider' }, ...items] : [sentinel];
    },
    // Bound-field picker change — intercept the ＋ sentinel (open the create-flag
    // panel), otherwise resolve/bind the picked field exactly as before.
    onBoundFieldSelect(idx, value) {
      if (value === CREATE_FLAG_VALUE) {
        this.openCreateFlag('bound', idx);
        return;
      }
      this.setBoundFieldTarget(idx, value);
    },
    // Visibility-condition field picker change — intercept the ＋ sentinel, otherwise
    // set the row's field (mirrors the previous v-model binding).
    onConditionFieldSelect(idx, value) {
      if (value === CREATE_FLAG_VALUE) {
        this.openCreateFlag('condition', idx);
        return;
      }
      const cond = this.vwConditions[idx];
      if (cond) cond.field = value == null ? null : value;
    },
    // ── Inline create-a-flag panel ─────────────────────────────────────────
    // Opens the panel and remembers which picker (bound-field row / condition row)
    // to bind the created flag back to.
    openCreateFlag(source, idx) {
      this.flagCtx = { source, idx };
      this.newFlag = { label: '', key: '', keyEdited: false };
      this.flagError = this.configVersionId == null
        ? 'คำถามนี้ยังไม่ได้ผูกกับ config version — เปิดจากหน้าแก้คำถามที่มีเวอร์ชันก่อนจึงจะสร้าง flag ได้'
        : '';
      this.creatingFlag = false;
      this.showCreateFlag = true;
    },
    cancelCreateFlag() {
      if (this.creatingFlag) return;
      this.showCreateFlag = false;
      this.flagCtx = null;
      this.flagError = '';
    },
    // lower_snake slug for the flag key (a-z, 0-9, single underscores, trimmed, ≤50).
    slugifyKey(text) {
      return String(text == null ? '' : text)
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
        .slice(0, 50);
    },
    onFlagLabelInput(value) {
      this.newFlag.label = value;
      // Auto-derive the key from the label until the admin edits the key by hand.
      if (!this.newFlag.keyEdited) this.newFlag.key = this.slugifyKey(value);
    },
    onFlagKeyInput(value) {
      this.newFlag.keyEdited = true;
      // Light-touch normalize while typing (allow a trailing "_"); the full slug
      // canon runs on submit via slugifyKey.
      this.newFlag.key = String(value == null ? '' : value)
        .toLowerCase()
        .replace(/\s+/g, '_')
        .replace(/[^a-z0-9_]/g, '');
    },
    async submitCreateFlag() {
      if (this.creatingFlag) return;
      if (this.configVersionId == null) {
        this.flagError = 'คำถามนี้ยังไม่ได้ผูกกับ config version สำหรับสร้าง flag';
        return;
      }
      const label = String(this.newFlag.label || '').trim();
      const key = this.slugifyKey(this.newFlag.key);
      if (!label) { this.flagError = 'กรุณากรอกชื่อ flag (ภาษาไทย)'; return; }
      if (!key) { this.flagError = 'กรุณากรอก key (อักษร a-z, 0-9, _)'; return; }
      // Friendly local dup guard (RuleCode is UPPER_SNAKE; the server guards too).
      const upper = key.toUpperCase();
      if ((this.ruleOptions || []).some((o) => String(o.value || '').toUpperCase() === upper)) {
        this.flagError = `มี flag รหัส '${upper}' อยู่แล้วในเวอร์ชันนี้ — เปลี่ยน key`;
        return;
      }
      this.creatingFlag = true;
      this.flagError = '';
      try {
        const dto = await taxConfigApi.appendFlag(this.configVersionId, {
          label,
          ruleCode: key,
          type: 'boolean',
          categoryCode: 'CTRL',
        });
        // Push into the local pool (same shape loadRules produces) so both pickers
        // recompute and the new flag is immediately selectable / bindable.
        this.ruleOptions.push({
          value: dto.ruleCode,
          label: `${dto.ruleCode} — ${dto.label || ''}`.trim(),
          rule: dto,
        });
        // Bind it back to whichever picker requested the create.
        const ctx = this.flagCtx || {};
        if (ctx.source === 'condition') {
          const cond = this.vwConditions[ctx.idx];
          if (cond) cond.field = dto.ruleCode;
        } else {
          this.setBoundFieldTarget(ctx.idx, dto.ruleCode);
        }
        this.showCreateFlag = false;
        this.flagCtx = null;
      } catch (e) {
        this.flagError = (e && e.message) || 'สร้าง flag ไม่สำเร็จ';
      } finally {
        this.creatingFlag = false;
      }
    },
    // 🆕 INCOME_CLASSIFIER category-catalog row helpers (mirror the boundFields list).
    // A row shape is { code, labelTh, flagKey, keywords[] } — same keys the store
    // round-trips (buildItemRequest / mapItemDtoToUi). code/flagKey stay verbatim
    // (never uppercased); buildItemRequest applies trim-only on save.
    addIncomeCategory() {
      if (!Array.isArray(this.item.incomeCategories)) this.item.incomeCategories = [];
      this.item.incomeCategories.push({ code: '', labelTh: '', flagKey: '', keywords: [] });
    },
    removeIncomeCategory(idx) {
      if (!Array.isArray(this.item.incomeCategories)) { this.item.incomeCategories = []; return; }
      this.item.incomeCategories.splice(idx, 1);
    },
    moveIncomeCategory(idx, dir) {
      if (!Array.isArray(this.item.incomeCategories)) { this.item.incomeCategories = []; return; }
      const target = idx + dir;
      if (target < 0 || target >= this.item.incomeCategories.length) return;
      const rows = this.item.incomeCategories;
      [rows[idx], rows[target]] = [rows[target], rows[idx]];
    },
    togglePnd(code) {
      if (!Array.isArray(this.item.pndCodes)) this.item.pndCodes = [];
      const i = this.item.pndCodes.indexOf(code);
      if (i >= 0) this.item.pndCodes.splice(i, 1);
      else this.item.pndCodes.push(code);
    },
    closeDialog() {
      this.$emit('update:modelValue', false);
    },
    submit() {
      this.submitAttempted = true;
      if (this.validationErrors.length) return;
      this.materializeItemForSubmit();
      this.item.visibleWhen = this.builtVisibleWhen;
      this.$emit('submit', JSON.parse(JSON.stringify(this.item)));
    },
    // 🆕 R2 — translate the working item (shaped for editing) into the concrete
    // ItemKind + shape the store/backend expect for the chosen TYPE:
    //  • layout             → LAYOUT (inputType only)
    //  • income_classifier  → INCOME_CLASSIFIER (incomeCategories)
    //  • group              → GROUP_INPUT (boundFields[])
    //  • single-field types → SCALAR: RULE_INPUT (top-level ruleCode) when bound to a
    //                         Tax Rule, else PERSONAL_INFO/SPECIAL_INPUT (top-level
    //                         fieldKey). No boundFields — nothing dropped, the single
    //                         binding becomes the scalar binding.
    materializeItemForSubmit() {
      const t = this.qType;
      if (t === 'confirm') {
        this.item.itemKind = 'CONFIRM';
        this.item.ruleCode = null;
        this.item.fieldKey = this.item.fieldKey ? String(this.item.fieldKey).trim() : null;
        this.item.boundFields = [];
        this.item.incomeCategories = [];
        this.item.options = null;
        this.item.answerOptions = [];
        this.item.answerType = null;
        this.item.controlType = null;
        return;
      }
      if (t === 'layout') {
        this.item.itemKind = 'LAYOUT';
        this.item.ruleCode = null;
        this.item.fieldKey = null;
        this.item.boundFields = [];
        this.item.incomeCategories = [];
        this.item.answerType = null;
        this.item.controlType = null;
        return;
      }
      if (t === 'income_classifier') {
        this.item.itemKind = 'INCOME_CLASSIFIER';
        this.item.ruleCode = null;
        this.item.fieldKey = null;
        this.item.boundFields = [];
        this.item.answerType = null;
        this.item.controlType = null;
        return;
      }
      if (t === 'group') {
        this.item.itemKind = 'GROUP_INPUT';
        this.item.ruleCode = null;
        this.item.fieldKey = null;
        this.item.incomeCategories = [];
        this.item.answerType = null;
        if (this.item.controlType !== 'chat_prompt') this.item.controlType = 'text_input';
        return;
      }
      // single-field → scalar
      const bf = (this.item.boundFields || [])[0] || {};
      const rc = bf.ruleCode ? String(bf.ruleCode).trim().toUpperCase() : null;
      const fk = bf.fieldKey ? String(bf.fieldKey).trim() : null;
      let fieldKind = String(bf.fieldKind || '').trim().toUpperCase();
      if (rc) {
        this.item.itemKind = 'RULE_INPUT';
        this.item.ruleCode = rc;
        this.item.fieldKey = null;
      } else if (fk) {
        if (fieldKind !== 'PERSONAL_INFO' && fieldKind !== 'SPECIAL_INPUT') {
          fieldKind = SPECIAL_INPUT_FIELD_KEYS.includes(fk) ? 'SPECIAL_INPUT' : 'PERSONAL_INFO';
        }
        this.item.itemKind = fieldKind;
        this.item.fieldKey = fk;
        this.item.ruleCode = null;
      }
      // Scalar items never carry boundFields / income categories.
      this.item.boundFields = [];
      this.item.incomeCategories = [];
      // The single binding's own "required" toggle becomes the item-level flag.
      if (bf && Object.prototype.hasOwnProperty.call(bf, 'isRequired')) {
        this.item.isRequired = !!bf.isRequired;
      }
      // Widget from the TYPE (preserving number / checkbox_group variants).
      this.applySingleFieldWidget();
    },
  },
};
</script>

<style scoped>
.tq-label {
  display: block;
  font-size: 0.85rem;
  font-weight: 500;
  color: rgba(var(--v-theme-on-surface), 0.82);
}

.tq-label-flag {
  color: rgb(var(--v-theme-warning));
  font-weight: 600;
}

.tq-block {
  margin-top: 8px;
  margin-bottom: 8px;
  padding: 16px;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  border-radius: 10px;
  background: rgba(var(--v-theme-on-surface), 0.015);
}

.tq-block-title {
  margin: 0 0 12px;
  font-size: 0.95rem;
  font-weight: 700;
  color: rgb(var(--v-theme-primary));
}

.tq-options-block {
  margin-top: 8px;
}

.tq-options-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.tq-option-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.tq-option-row {
  display: flex;
  align-items: center;
  gap: 6px;
}

.tq-option-index {
  flex: 0 0 24px;
  text-align: center;
  font-size: 0.8rem;
  color: rgba(var(--v-theme-on-surface), 0.6);
}

/* GROUP_INPUT bound-field rows — target picker + required toggle side by side,
   with move/remove controls trailing (mirrors the answerOptions row layout). */
.tq-boundfields-block {
  margin-top: 4px;
}

/* CONFIRM checkpoint editor — confirm key + the two authored prompts, stacked. */
.tq-confirm-block {
  margin-top: 4px;
}

.tq-boundfield-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 4px;
}

.tq-boundfield-row {
  display: flex;
  align-items: center;
  gap: 6px;
}

.tq-boundfield-main {
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  flex-wrap: wrap;
}

.tq-boundfield-main > .v-select,
.tq-boundfield-main > .v-autocomplete {
  flex: 1 1 220px;
  min-width: 180px;
}

.tq-boundfield-required {
  flex: 0 0 auto;
}

/* INCOME_CLASSIFIER category rows — the four editable fields (code / labelTh /
   flagKey / keywords) wrap in a flex block, with move/remove controls trailing
   (mirrors the boundFields row layout). */
.tq-income-block {
  margin-top: 4px;
}

.tq-income-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 4px;
}

.tq-income-row {
  display: flex;
  align-items: flex-start;
  gap: 6px;
}

.tq-income-row > .tq-option-index {
  margin-top: 12px;
}

.tq-income-main {
  flex: 1 1 auto;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  min-width: 0;
  flex-wrap: wrap;
}

.tq-income-code {
  flex: 1 1 120px;
  min-width: 110px;
}

.tq-income-label {
  flex: 2 1 200px;
  min-width: 160px;
}

.tq-income-flag {
  flex: 1 1 160px;
  min-width: 140px;
}

.tq-income-keywords {
  flex: 1 1 100%;
  min-width: 200px;
}

.tq-income-actions {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  margin-top: 4px;
}

.tq-pnd-block {
  margin-top: 8px;
}

.tq-pnd-legend {
  margin: 6px 0 0;
  padding-left: 18px;
  font-size: 0.78rem;
  color: rgba(var(--v-theme-on-surface), 0.6);
}

.tq-prompt-panel {
  margin-top: 8px;
}

.tq-error-list {
  margin: 0;
  padding-left: 18px;
  font-size: 0.85rem;
}

.tq-vw-toggle {
  flex-wrap: wrap;
}

.tq-vw-matchmode {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 12px;
  margin-bottom: 12px;
}

.tq-vw-matchmode-label {
  font-size: 0.8rem;
  color: rgba(var(--v-theme-on-surface), 0.6);
}

.tq-vw-row {
  margin-bottom: 2px;
}

/* Inline "และ / หรือ" connector between condition rows — makes the AND/OR
   relationship legible where the reader is looking, not only in the toggle. */
.tq-vw-join-sep {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 2px 0 6px;
}

.tq-vw-join-sep::before,
.tq-vw-join-sep::after {
  content: '';
  flex: 1 1 auto;
  height: 1px;
  background: rgba(var(--v-theme-on-surface), 0.08);
}

.tq-vw-join-word {
  flex: 0 0 auto;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: rgb(var(--v-theme-primary));
  background: rgba(var(--v-theme-primary), 0.08);
  border-radius: 999px;
  padding: 1px 12px;
}

.tq-vw-add {
  margin: 6px 0 10px;
}

.tq-vw-preview {
  margin-top: 2px;
}

.tq-vw-preview-note {
  margin-top: 4px;
  font-size: 0.78rem;
  color: rgba(var(--v-theme-on-surface), 0.6);
}

.tq-vw-code {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  background: rgba(var(--v-theme-on-surface), 0.06);
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 0.85rem;
}

/* Add/remove a condition row: brief slide-fade so the change registers. */
.tq-vw-list-enter-active,
.tq-vw-list-leave-active {
  transition: opacity 200ms cubic-bezier(0.22, 1, 0.36, 1),
    transform 200ms cubic-bezier(0.22, 1, 0.36, 1);
}

.tq-vw-list-enter-from,
.tq-vw-list-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

@media (prefers-reduced-motion: reduce) {
  .tq-vw-list-enter-active,
  .tq-vw-list-leave-active {
    transition: none;
  }
}
</style>
